"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { ArrowRight, CircleCheck, FileText, ShieldCheck, SlidersHorizontal } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface HeroCopy {
  categoryBadge: string;
  standardBadge: string;
  title: string;
  lead: string;
  relatedLabel: string;
  imageAlt: string;
  imageBadge: string;
  cardNote: string;
  cardLink: string;
  specs: {
    label: string;
    value: string;
  }[];
}
const copyData: Record<Lang, HeroCopy> = {
  en: {
    categoryBadge: "Sintered Wire Mesh",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "Sintered Wire Mesh Selection Guide",
    lead: "Select the optimal diffusion-bonded wire mesh laminate for high-pressure differential filtration, hot gas sparging, and demanding solid-liquid separation. Evaluate micron rating, layer architecture, and mechanical stability across 1 to 200 µm.",
    relatedLabel: "Product Selection Guide",
    imageAlt: "Multilayer diffusion-bonded sintered wire mesh laminate cross section and pore structure",
    imageBadge: "5-Layer Sintered Laminate",
    cardNote: "Discs, cylinders & custom fabricated shapes",
    cardLink: "Multilayer Specs",
    specs: [{
      label: "Micron Rating Range",
      value: "1 – 200 µm"
    }, {
      label: "Layer Configurations",
      value: "2 to 7 bonded layers"
    }, {
      label: "Standard Materials",
      value: "AISI 304, 304L, 316, 316L"
    }, {
      label: "Pore Stability",
      value: "Fixed geometry, diffusion-bonded"
    }]
  },
  zh: {
    categoryBadge: "烧结丝网",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "烧结丝网选型指南",
    lead: "为高压差过滤、高温气体鼓泡及严苛固液分离工艺选择最佳扩散焊接丝网层合结构。在 1 至 200 微米范围内评估过滤精度、层叠架构与机械稳定性。",
    relatedLabel: "产品选型指南",
    imageAlt: "多层扩散焊接烧结丝网层合截面结构与孔隙微观形态",
    imageBadge: "5层标准复合烧结板",
    cardNote: "支持滤圆片、圆筒滤芯与异型定制组件",
    cardLink: "多层网参数",
    specs: [{
      label: "过滤精度范围",
      value: "1 – 200 µm"
    }, {
      label: "层合结构配置",
      value: "2 至 7 层固态扩散烧结"
    }, {
      label: "标准合金材质",
      value: "AISI 304, 304L, 316, 316L"
    }, {
      label: "孔隙几何稳定性",
      value: "固相熔接，孔径刚性固定"
    }]
  },
  es: {
    categoryBadge: "Malla sinterizada",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "Guía de selección de malla de alambre sinterizada",
    lead: "Seleccione la estructura laminada de malla sinterizada por difusión adecuada para filtración con alta presión diferencial y separación sólido-líquido exigente. Evalúe micraje, arquitectura de capas y estabilidad mecánica entre 1 y 200 µm.",
    relatedLabel: "Guía de selección",
    imageAlt: "Estructura de sección transversal y poros de malla sinterizada multicapa",
    imageBadge: "Laminado estándar de 5 capas",
    cardNote: "Discos, cartuchos cilíndricos y formatos a medida",
    cardLink: "Parámetros multicapa",
    specs: [{
      label: "Rango de micras",
      value: "1 – 200 µm"
    }, {
      label: "Configuración de capas",
      value: "2 a 7 capas sinterizadas"
    }, {
      label: "Materiales estándar",
      value: "AISI 304, 304L, 316, 316L"
    }, {
      label: "Estabilidad de poro",
      value: "Geometría rígida sin migración"
    }]
  },
  de: {
    categoryBadge: "Sinterdrahtgewebe",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "Auswahlleitfaden für Sintermetallgewebe",
    lead: "Wählen Sie das optimale diffusionsgebundene Drahtgewebelaminat für hohe Differenzdrücke, Heißgasfiltration und anspruchsvolle Fest-Flüssig-Trennung. Vergleichen Sie Feinheit, Lagenaufbau und Stabilität von 1 bis 200 µm.",
    relatedLabel: "Produktauswahl",
    imageAlt: "Querschnitt und Porenstruktur von mehrlagigem Sinterdrahtgewebe",
    imageBadge: "5-lagiges Standard-Laminat",
    cardNote: "Ronden, Filterzylinder und kundenspezifische Formteile",
    cardLink: "Mehrlagen-Daten",
    specs: [{
      label: "Filterfeinheit",
      value: "1 – 200 µm"
    }, {
      label: "Lagenkonfiguration",
      value: "2 bis 7 diffusionsgebundene Lagen"
    }, {
      label: "Standard-Werkstoffe",
      value: "AISI 304, 304L, 316, 316L"
    }, {
      label: "Porenstabilität",
      value: "Fixierte Geometrie, kein Drahtverzug"
    }]
  },
  fr: {
    categoryBadge: "Toile métallique frittée",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "Guide de sélection de toile métallique frittée",
    lead: "Choisissez la structure multicouche frittée par diffusion optimale pour la filtration sous haute pression différentielle et la séparation solide-liquide exigeante. Évaluez le seuil de filtration, le nombre de couches et la stabilité mécanique de 1 à 200 µm.",
    relatedLabel: "Guide de sélection",
    imageAlt: "Structure en coupe transversale et géométrie des pores de toile frittée multicouche",
    imageBadge: "Stratifié standard 5 couches",
    cardNote: "Disques, cylindres filtrants et pièces sur mesure",
    cardLink: "Fiche multicouche",
    specs: [{
      label: "Seuil de filtration",
      value: "1 – 200 µm"
    }, {
      label: "Structure des couches",
      value: "2 à 7 couches liées par diffusion"
    }, {
      label: "Nuances standard",
      value: "AISI 304, 304L, 316, 316L"
    }, {
      label: "Stabilité des pores",
      value: "Géométrie rigide sans migration"
    }]
  },
  pt: {
    categoryBadge: "Malha de arame sinterizada",
    standardBadge: "ISO 9044 / ASTM E2016",
    title: "Guia de seleção de malha de arame sinterizada",
    lead: "Selecione o laminado sinterizado por difusão adequado para processos com alta pressão diferencial, aeração a quente e separação sólido-líquido crítica. Avalie micragem, arranjo de camadas e resistência mecânica de 1 a 200 µm.",
    relatedLabel: "Guia de seleção",
    imageAlt: "Estrutura em corte transversal e arquitetura de poros de malha sinterizada multicamadas",
    imageBadge: "Laminado padrão de 5 camadas",
    cardNote: "Discos, cilindros e formatos especiais sob medida",
    cardLink: "Dados multicamadas",
    specs: [{
      label: "Faixa de micragem",
      value: "1 – 200 µm"
    }, {
      label: "Configuração de camadas",
      value: "2 a 7 camadas sinterizadas"
    }, {
      label: "Ligas padrão",
      value: "AISI 304, 304L, 316, 316L"
    }, {
      label: "Estabilidade de poros",
      value: "Geometria fixa, ligação por difusão"
    }]
  }
};
export default function SinteredHero() {
  const {
    lang
  } = useLanguage();
  const content = pick(lang, copyData);
  const quoteLabel = pick(lang, COMMON.requestQuote);
  return <section id="sinteredhero" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
          {/* Left Column (7 Columns on lg) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start">
            {/* Exactly two badges: category (bg-card) + standard (bg-muted) */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                <span>{content.categoryBadge}</span>
              </Badge>
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono mf-caption text-foreground">
                <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                <span>{content.standardBadge}</span>
              </Badge>
            </div>

            {/* Main Headline - exactly one h1 per page */}
            <h1 className="font-default text-balance mf-h1 text-foreground">
              {content.title}
            </h1>

            {/* Lead Paragraph */}
            <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
              {content.lead}
            </p>

            {/* CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild>
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{quoteLabel}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" asChild>
                <Link to="/ProductSelectionPage" className="inline-flex items-center gap-2">
                  <FileText className="size-4" aria-hidden="true" />
                  <span>{content.relatedLabel}</span>
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column: Spec Display Card (5 Columns on lg) */}
          <Reveal delay={50} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
              {/* Product Visual Container */}
              <div className="mf-card-media mf-ratio-hero w-full">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-photograph-showing-the-layered-str-0f200c.png" alt={content.imageAlt} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover" />
                <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono flex items-center gap-1.5 text-foreground">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{content.imageBadge}</span>
                </div>
              </div>

              {/* Single Footer Line */}
              <CardContent className="p-4">
                <div className="flex items-center justify-between mf-caption">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <CircleCheck className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                    <span>{content.cardNote}</span>
                  </div>
                  <Link to="/SinteredMultilayerMeshPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                    <span>{content.cardLink}</span>
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Spec Strip directly below grid */}
        <div className="mf-spec-strip">
          {content.specs.map((item, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{item.label}</span>
              <span className="mf-spec-strip__value">{item.value}</span>
            </div>)}
        </div>
      </div>
    </section>;
}